import { useEffect } from "react";

/**
 * usePageMeta — declaratively set per-route <title>, meta description, and
 * canonical URL from any React component.
 *
 * Why this exists: the SEO audit (2026-06-27) flagged 22 pages sharing the
 * same global <title> from index.html and 25 pages sharing the same meta
 * description. Without per-route metadata, Google can't differentiate the
 * marketing landing from the studio portfolio from the booking page — they
 * all rank as "the same page" in its index. Same problem for OG/social
 * previews when someone shares a deep link.
 *
 * Implementation choice: zero-dependency. We just mutate the document head
 * via useEffect. react-helmet-async is nicer for SSR scenarios but this app
 * is CSR-only, and the audit's renderer (and Google's Web Rendering Service)
 * runs JS — so document.title set in useEffect IS what they index.
 *
 * @param {object} opts
 * @param {string} opts.title       — full document title (we don't auto-append the brand suffix)
 * @param {string} [opts.description] — meta description (120-160 chars ideal)
 * @param {string} [opts.canonical]   — absolute canonical URL (use to dedupe near-identical routes)
 * @param {string} [opts.ogImage]     — absolute OG image URL (defaults to whatever index.html sets)
 * @param {string} [opts.ogType]      — defaults to "website"
 * @param {boolean} [opts.noIndex]    — add <meta name="robots" content="noindex"> for admin/auth pages
 */
export function usePageMeta({
  title,
  description,
  canonical,
  ogImage,
  ogType,
  noIndex,
} = {}) {
  useEffect(() => {
    if (!title && !description && !canonical && !noIndex) return;

    const prevTitle = document.title;
    if (title) document.title = title;

    // Helper: upsert a <meta name=...> or <meta property=...> tag
    const upsertMeta = (selector, attr, attrValue, content) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, created };
    };

    // Helper: upsert <link rel=canonical>
    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };

    const restores = [];

    if (description) {
      const r = upsertMeta('meta[name="description"]', "name", "description", description);
      restores.push(() => (r.created ? r.el.remove() : r.el.setAttribute("content", r.prev || "")));
      const og = upsertMeta('meta[property="og:description"]', "property", "og:description", description);
      restores.push(() => (og.created ? og.el.remove() : og.el.setAttribute("content", og.prev || "")));
      const tw = upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
      restores.push(() => (tw.created ? tw.el.remove() : tw.el.setAttribute("content", tw.prev || "")));
    }

    if (title) {
      const og = upsertMeta('meta[property="og:title"]', "property", "og:title", title);
      restores.push(() => (og.created ? og.el.remove() : og.el.setAttribute("content", og.prev || "")));
      const tw = upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
      restores.push(() => (tw.created ? tw.el.remove() : tw.el.setAttribute("content", tw.prev || "")));
    }

    if (ogImage) {
      const og = upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
      restores.push(() => (og.created ? og.el.remove() : og.el.setAttribute("content", og.prev || "")));
      const tw = upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
      restores.push(() => (tw.created ? tw.el.remove() : tw.el.setAttribute("content", tw.prev || "")));
    }

    if (ogType) {
      const r = upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);
      restores.push(() => (r.created ? r.el.remove() : r.el.setAttribute("content", r.prev || "")));
    }

    if (canonical) {
      const r = upsertLink("canonical", canonical);
      restores.push(() => (r.created ? r.el.remove() : r.el.setAttribute("href", r.prev || "")));
      const og = upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
      restores.push(() => (og.created ? og.el.remove() : og.el.setAttribute("content", og.prev || "")));
    }

    if (noIndex) {
      const r = upsertMeta('meta[name="robots"]', "name", "robots", "noindex,nofollow");
      restores.push(() => (r.created ? r.el.remove() : r.el.setAttribute("content", r.prev || "")));
    }

    return () => {
      document.title = prevTitle;
      // Restore in reverse so the head reverts to its prior shape cleanly
      restores.reverse().forEach((fn) => {
        try { fn(); } catch (_) { /* DOM may already be gone on hot reload */ }
      });
    };
  }, [title, description, canonical, ogImage, ogType, noIndex]);
}

export default usePageMeta;
